import {
  forwardRef,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn.js';
import { remask } from './masked-text.js';

export interface MaskedInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  label: string;
  value: string;
  onValueChange: (text: string) => void;
  format: (digits: string, international: boolean) => string;
  error?: string | undefined;
  /// Linha abaixo do campo quando não há erro. `hintTone` diz se é aviso — um número que parece
  /// errado mas pode ser salvo — ou só explicação.
  hint?: ReactNode | undefined;
  hintTone?: 'muted' | 'positive' | 'warning' | undefined;
  /// Selo à esquerda do texto, como o país do telefone.
  badge?: string | undefined;
}

const hintTones = {
  muted: 'text-content-muted',
  positive: 'text-positive',
  warning: 'text-warning',
} as const;

/// Campo que reformata o texto a cada tecla pela `format` recebida. A base de `PhoneInput` e
/// `DocumentInput`; sozinho serve a qualquer número com máscara.
export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  (
    {
      badge,
      className,
      error,
      format,
      hint,
      hintTone = 'muted',
      id: providedId,
      label,
      onValueChange,
      value,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
    const inputRef = useRef<HTMLInputElement>(null);
    const pendingCaret = useRef<number | null>(null);
    useImperativeHandle(ref, () => inputRef.current!, []);
    useLayoutEffect(() => {
      const input = inputRef.current;
      if (pendingCaret.current === null || !input || document.activeElement !== input) return;
      input.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }, [value]);

    return (
      <div className="min-w-0 space-y-2">
        <label htmlFor={id} className="block text-xs font-semibold text-content/70">
          {label}
        </label>
        <div className="relative min-w-0">
          {badge ? (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-3 my-auto h-fit rounded-md bg-content-secondary/15 px-1.5 py-0.5 text-[11px] font-bold text-content-secondary"
            >
              {badge}
            </span>
          ) : null}
          <input
            ref={inputRef}
            id={id}
            autoComplete="off"
            value={value}
            onChange={(event) => {
              const next = remask(
                event.target.value,
                value,
                event.target.selectionStart ?? event.target.value.length,
                format,
              );
              pendingCaret.current = next.caret;
              onValueChange(next.text);
            }}
            aria-invalid={Boolean(error)}
            aria-describedby={descriptionId}
            className={cn(
              'hue-input h-12 w-full rounded-xl border bg-surface-elevated px-4 text-sm tabular-nums text-content outline-none transition placeholder:text-content-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20',
              badge && 'pl-13',
              error ? 'border-danger/70' : 'border-surface-border',
              className,
            )}
            {...props}
          />
        </div>
        {error ? (
          <p id={descriptionId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={descriptionId} className={cn('text-xs leading-5', hintTones[hintTone])}>
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
MaskedInput.displayName = 'MaskedInput';
